import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";
import { FaUser, FaChevronDown, FaBriefcase, FaRegBookmark, FaCrown } from "react-icons/fa";
import { IoBagOutline } from "react-icons/io5";
import { TfiHelpAlt } from "react-icons/tfi";
import { IoExitOutline } from "react-icons/io5";
import { HiOutlineBriefcase, HiMiniBuildingOffice2 } from "react-icons/hi2";
import { FiDownload } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { logout } from "../../Redux/Slices/AuthSlice";
import BrandWordmark from "./BrandWordmark";
import { jwtDecode } from "jwt-decode";
import { useEffect, useRef, useState } from "react";
import NotificationBell from "./NotificationBell";
import { PLAY_STORE_URL } from "../../constants/appLinks";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [jobsDropdownOpen, setJobsDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const userMenuRef = useRef(null);

  let LogoutToggle = JSON.parse(localStorage.getItem("token"));
  let token = localStorage.getItem("token");
  const name = localStorage.getItem("name");

  useEffect(() => {
    let decodedToken;
    if (token) {
      try {
        decodedToken = jwtDecode(token);
        setRole(decodedToken?.role?.name);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.clear();
      }
    } else {
      setIsAuthenticated(false);
    }

    const storedAvatar =
      localStorage.getItem("profile_image") ||
      localStorage.getItem("profilePic") ||
      "";
    setProfileImage(storedAvatar);
    setAvatarLoadFailed(false);
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!name) return "U";
    const names = name.trim().split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  // Get role badge color
  const getRoleBadgeClass = () => {
    switch (role) {
      case "Admin":
        return "role-badge-admin";
      case "Employer":
        return "role-badge-employer";
      case "Jobseeker":
        return "role-badge-jobseeker";
      default:
        return "role-badge-default";
    }
  };

  const getRoleDisplayName = () => {
    if (role === "Jobseeker") return "Member";
    return role || "User";
  };

  const log = () => {
    dispatch(logout());
    navigate("/signin");
    setUserDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isActive = (path) => {
    if (path === "/jobs") {
      return [
        "/jobs",
        "/ca-jobs",
        "/cs-jobs",
        "/cma-jobs",
        "/featured-job",
        "/career-sites",
        "/third-party-listing",
      ].includes(location.pathname);
    }
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const renderCompactRoleTrigger = () => {
    if (role === "Admin") {
      return (
        <span className="modern-navbar-admin-trigger">
          <FaCrown className="modern-navbar-admin-icon" />
          <span className="modern-navbar-admin-label">Admin</span>
        </span>
      );
    }

    if (role === "Employer") {
      return (
        <span className="modern-navbar-employer-trigger">
          <HiMiniBuildingOffice2 className="modern-navbar-employer-icon" />
          <span className="modern-navbar-employer-label">Employer</span>
        </span>
      );
    }

    return <FaUser className="modern-navbar-profile-icon" />;
  };

  const renderDownloadAppButton = (extraClassName = "") => (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`modern-navbar-btn modern-navbar-btn-download ${extraClassName}`.trim()}
      aria-label="Download CAJobPortal app"
      onClick={closeMobileMenu}
    >
      <FiDownload />
      <span className="modern-navbar-btn-text">Download App</span>
      <span className="modern-navbar-btn-text-mobile">App</span>
    </a>
  );

  const renderUserAvatar = () => {
    const shouldShowImage = profileImage && !avatarLoadFailed;

    if (shouldShowImage) {
      return (
        <img
          src={profileImage}
          alt="User"
          className="modern-navbar-user-avatar"
          onError={() => setAvatarLoadFailed(true)}
        />
      );
    }

    return <div className="modern-navbar-user-initials">{getUserInitials()}</div>;
  };

  return (
    <>
      <nav className="modern-navbar">
        <div className="modern-navbar-container">
          {/* Logo */}
          <Link to="/" className="modern-navbar-logo" onClick={closeMobileMenu}>
            <BrandWordmark variant="navbar" />
          </Link>

          {/* Desktop Navigation Links */}
          <ul className="modern-navbar-links">
            <li
              className={`modern-navbar-dropdown ${jobsDropdownOpen || isActive("/jobs") ? "active" : ""}`}
              onMouseEnter={() => setJobsDropdownOpen(true)}
              onMouseLeave={() => setJobsDropdownOpen(false)}
            >
              <Link to="/jobs" className={`modern-navbar-link ${isActive("/jobs") ? "active" : ""}`}>
                Jobs
                <FaChevronDown className="modern-navbar-dropdown-icon" />
              </Link>
              <div className="modern-navbar-dropdown-menu modern-navbar-jobs-menu">
                <Link to="/jobs" onClick={() => setJobsDropdownOpen(false)}>
                  All Jobs
                </Link>
                <Link to="/ca-jobs" onClick={() => setJobsDropdownOpen(false)}>
                  CA Jobs
                </Link>
                <Link to="/cs-jobs" onClick={() => setJobsDropdownOpen(false)}>
                  CS Jobs
                </Link>
                <Link to="/cma-jobs" onClick={() => setJobsDropdownOpen(false)}>
                  CMA Jobs
                </Link>
                <Link to="/featured-job" onClick={() => setJobsDropdownOpen(false)}>
                  Featured Jobs
                </Link>
                <Link to="/career-sites" onClick={() => setJobsDropdownOpen(false)}>
                  PSU / Career Site Jobs
                </Link>
                <Link to="/post-job" onClick={() => setJobsDropdownOpen(false)}>
                  Post a Job
                </Link>
              </div>
            </li>
            {!(isAuthenticated && role === "Jobseeker") && (
              <li>
                <Link
                  to="/post-job"
                  className={`modern-navbar-link ${isActive("/post-job") || isActive("/recruiter") ? "active" : ""}`}
                >
                  Post a Job
                </Link>
              </li>
            )}
            <li
              className={`modern-navbar-dropdown ${resourcesDropdownOpen ? "active" : ""}`}
              onMouseEnter={() => setResourcesDropdownOpen(true)}
              onMouseLeave={() => setResourcesDropdownOpen(false)}
            >
              <button className="modern-navbar-dropdown-toggle">
                Resources
                <FaChevronDown className="modern-navbar-dropdown-icon" />
              </button>
              <div className="modern-navbar-dropdown-menu">
                <Link to="/about" onClick={() => setResourcesDropdownOpen(false)}>
                  About Us
                </Link>
                <Link to="/contact" onClick={() => setResourcesDropdownOpen(false)}>
                  Contact
                </Link>
                <a
                  href="https://blog.cajobportal.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setResourcesDropdownOpen(false)}
                >
                  Blog
                </a>
              </div>
            </li>
          </ul>

          {/* Desktop Actions */}
          <div className="modern-navbar-actions">
            {!isAuthenticated ? (
              <>
                <Link to="/signin" className="modern-navbar-btn modern-navbar-btn-text-link">
                  Sign In
                </Link>
                <span className="modern-navbar-divider">|</span>
                {renderDownloadAppButton()}
                <Link to="/signup-recruiter" className="modern-navbar-btn modern-navbar-btn-employer">
                  <HiOutlineBriefcase />
                  <span className="modern-navbar-btn-text">Recruiter Login</span>
                  <span className="modern-navbar-btn-text-mobile">Recruiters</span>
                </Link>
              </>
            ) : (
              <>
                {renderDownloadAppButton()}
                {role === "Jobseeker" && (
                  <div className="modern-navbar-quick-actions" aria-label="Quick actions">
                    <Link
                      to="/jobseeker/applied-jobs"
                      state={{ tab: "saved" }}
                      className="modern-navbar-icon-link"
                      title="Saved Jobs"
                    >
                      <FaRegBookmark />
                    </Link>
                    <Link
                      to="/jobseeker/applied-jobs"
                      state={{ tab: "applied" }}
                      className="modern-navbar-icon-link"
                      title="My Jobs"
                    >
                      <IoBagOutline />
                    </Link>
                  </div>
                )}

                <NotificationBell variant="modern" />

                <div
                  className={`modern-navbar-user ${role === "Jobseeker" || role === "Admin" || role === "Employer" ? "icon-only" : ""} ${userDropdownOpen ? "active" : ""}`}
                  ref={userMenuRef}
                  onMouseEnter={() => setUserDropdownOpen(true)}
                  onMouseLeave={() => setUserDropdownOpen(false)}
                >
                  <button
                    className="modern-navbar-user-toggle"
                    onClick={() => setUserDropdownOpen((prev) => !prev)}
                  >
                    {role === "Jobseeker" || role === "Admin" || role === "Employer" ? (
                      <>
                        {renderCompactRoleTrigger()}
                      </>
                    ) : (
                      <>
                        <div className="modern-navbar-user-avatar-wrapper">
                          {renderUserAvatar()}
                          <span className="modern-navbar-user-status" aria-hidden="true" />
                        </div>
                        <div className="modern-navbar-user-info">
                          <span className="modern-navbar-user-name">{name || "User"}</span>
                          <span className={`modern-navbar-role-badge ${getRoleBadgeClass()}`}>
                            {getRoleDisplayName()}
                          </span>
                        </div>
                      </>
                    )}
                  </button>

                  <div className="modern-navbar-user-menu">
                    {role === "Admin" && (
                      <>
                        <Link to="/admin/profile" onClick={() => setUserDropdownOpen(false)}>
                          <FaUser /> My Profile
                        </Link>
                        <Link to="/admin/dashboard" onClick={() => setUserDropdownOpen(false)}>
                          <FaBriefcase /> Dashboard
                        </Link>
                        <Link to="/contact" onClick={() => setUserDropdownOpen(false)}>
                          <TfiHelpAlt /> Help Center
                        </Link>
                        <div className="modern-navbar-user-menu-divider" />
                        <button onClick={log} className="logout-menu-btn">
                          <IoExitOutline /> Log Out
                        </button>
                      </>
                    )}
                    {role === "Employer" && (
                      <>
                        <Link to="/employer/profile" onClick={() => setUserDropdownOpen(false)}>
                          <FaUser /> My Profile
                        </Link>
                        <Link to="/employer/posted-Jobs" onClick={() => setUserDropdownOpen(false)}>
                          <IoBagOutline /> Posted Jobs
                        </Link>
                        <Link to="/contact" onClick={() => setUserDropdownOpen(false)}>
                          <TfiHelpAlt /> Help Center
                        </Link>
                        <div className="modern-navbar-user-menu-divider" />
                        <button onClick={log} className="logout-menu-btn">
                          <IoExitOutline /> Log Out
                        </button>
                      </>
                    )}
                    {role === "Jobseeker" && (
                      <>
                        <Link to="/jobseeker/profile" onClick={() => setUserDropdownOpen(false)}>
                          <FaUser /> My Profile
                        </Link>
                        <Link to="/contact" onClick={() => setUserDropdownOpen(false)}>
                          <TfiHelpAlt /> Help Center
                        </Link>
                        <div className="modern-navbar-user-menu-divider" />
                        <button onClick={log} className="logout-menu-btn">
                          <IoExitOutline /> Log Out
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className={`modern-navbar-mobile-toggle ${mobileMenuOpen ? "active" : ""}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`modern-navbar-mobile-backdrop ${mobileMenuOpen ? "active" : ""}`}
        onClick={closeMobileMenu}
        aria-hidden={!mobileMenuOpen}
      />
      <div
        className={`modern-navbar-mobile-menu ${mobileMenuOpen ? "active" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <ul className="modern-navbar-mobile-links">
          <li>
            <Link to="/jobs" onClick={closeMobileMenu}>
              All Jobs
            </Link>
          </li>
          <li>
            <Link to="/ca-jobs" onClick={closeMobileMenu}>
              CA Jobs
            </Link>
          </li>
          <li>
            <Link to="/cs-jobs" onClick={closeMobileMenu}>
              CS Jobs
            </Link>
          </li>
          <li>
            <Link to="/cma-jobs" onClick={closeMobileMenu}>
              CMA Jobs
            </Link>
          </li>
          <li>
            <Link to="/featured-job" onClick={closeMobileMenu}>
              Featured Jobs
            </Link>
          </li>
          {!(isAuthenticated && role === "Jobseeker") && (
            <li>
              <Link to="/post-job" onClick={closeMobileMenu}>
                Post a Job
              </Link>
            </li>
          )}
          <li>
            <Link to="/about" onClick={closeMobileMenu}>
              About Us
            </Link>
          </li>
          <li>
            <Link to="/contact" onClick={closeMobileMenu}>
              Contact
            </Link>
          </li>
          <li>
            <a
              href="https://blog.cajobportal.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobileMenu}
            >
              Blog
            </a>
          </li>
        </ul>

        {!isAuthenticated ? (
          <div className="modern-navbar-mobile-actions">
            <Link
              to="/signin"
              className="modern-navbar-btn modern-navbar-btn-primary"
              onClick={closeMobileMenu}
            >
              Sign In
            </Link>
            {renderDownloadAppButton("modern-navbar-btn-download-drawer")}
            <Link
              to="/signup-recruiter"
              className="modern-navbar-btn modern-navbar-btn-secondary"
              onClick={closeMobileMenu}
            >
              <HiOutlineBriefcase /> Recruiter Login
            </Link>
          </div>
        ) : (
          <div className="modern-navbar-mobile-user-section">
            <div className="modern-navbar-mobile-user-info">
              <div className="modern-navbar-user-avatar-wrapper">
                {renderUserAvatar()}
                <span className="modern-navbar-user-status" aria-hidden="true" />
              </div>
              <div>
                <div className="modern-navbar-mobile-user-name">{name || "User"}</div>
                <div className={`modern-navbar-role-badge ${getRoleBadgeClass()}`}>
                  {getRoleDisplayName()}
                </div>
              </div>
            </div>
            
            <div className="modern-navbar-mobile-user-links">
              {role === "Admin" && (
                <>
                  <Link to="/admin/profile" onClick={closeMobileMenu}>
                    <FaUser /> My Profile
                  </Link>
                  <Link to="/admin/dashboard" onClick={closeMobileMenu}>
                    <FaBriefcase /> Dashboard
                  </Link>
                </>
              )}
              {role === "Employer" && (
                <>
                  <Link to="/employer/profile" onClick={closeMobileMenu}>
                    <FaUser /> My Profile
                  </Link>
                  <Link to="/employer/posted-Jobs" onClick={closeMobileMenu}>
                    <IoBagOutline /> Posted Jobs
                  </Link>
                </>
              )}
              {role === "Jobseeker" && (
                <>
                  <Link to="/jobseeker/applied-jobs" state={{ tab: "saved" }} onClick={closeMobileMenu}>
                    <FaRegBookmark /> Saved Jobs
                  </Link>
                  <Link to="/jobseeker/profile" onClick={closeMobileMenu}>
                    <FaUser /> My Profile
                  </Link>
                  <Link to="/jobseeker/applied-jobs" state={{ tab: "applied" }} onClick={closeMobileMenu}>
                    <IoBagOutline /> My Jobs
                  </Link>
                </>
              )}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                <FiDownload /> Download App
              </a>
              <Link to="/contact" onClick={closeMobileMenu}>
                <TfiHelpAlt /> Help Center
              </Link>
              <button onClick={log} className="mobile-logout-btn">
                <IoExitOutline /> Log Out
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Navbar;

